export const getTextFromObjectOrString = (v) => {
  if (v === null || v === undefined) {
    return '';
  }
  if (typeof v === 'object') {
    // if there is only one key and that is meta data key -> '@data-comment' then return empty string
    if (Object.keys(v).length === 1 && Object.keys(v)[0] === '@data-comment') {
      return '';
    }
    if (v['#text'] === undefined || v['#text'] === null) {
      return JSON.stringify(v, null, 2);
    }
    return v['#text'];
  }
  return v;
};

// Keys added by the workflow parser for display/layout purposes.
// These are NOT part of ConfigJson and must not be included in form data
// or wrapped in {#text} format during form-to-node transformation.
const WORKFLOW_METADATA_KEYS = new Set([
  'label',
  'details',
  'nodeType',
  'allReferences',
  'isOutput',
  'pathDepth',
  'comment',
]);

export const transformNodeDataToFormData = (nodeData) => {
  const formData = {};
  const copyOfNodeData = structuredClone(nodeData);
  if (copyOfNodeData) {
    if (copyOfNodeData['@id']) {
      formData['@id'] = copyOfNodeData['@id'];
      delete copyOfNodeData['@id'];
    }
    if (copyOfNodeData.name && copyOfNodeData.name['#text']) {
      formData.name = copyOfNodeData.name['#text'];
      delete copyOfNodeData.name;
    }
    if (copyOfNodeData.type && copyOfNodeData.type['#text']) {
      formData.type = copyOfNodeData.type['#text'];
      delete copyOfNodeData.type;
    }
  }

  if (copyOfNodeData?.properties) {
    formData.properties = {};
    Object.entries(copyOfNodeData.properties).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      formData.properties[key] = getTextFromObjectOrString(value);
    });
    delete copyOfNodeData.properties;
  }

  if (copyOfNodeData?.parameters) {
    formData.parameters = {};
    Object.entries(copyOfNodeData.parameters).forEach(([key, value]) => {
      formData.parameters[key] = value;
    });
    delete copyOfNodeData.parameters;
  }

  if (nodeData?.schema !== undefined && nodeData?.schema !== null) {
    const schemaText = getTextFromObjectOrString(nodeData.schema);

    // Add schema to formData (preserve all schema content, even empty objects)
    if (schemaText !== undefined && schemaText !== null) {
      formData.schema = schemaText;
    }
    delete copyOfNodeData.schema;
  }

  Object.entries(copyOfNodeData).forEach(([key, value]) => {
    // Skip workflow metadata keys — they are not ConfigJson fields
    if (WORKFLOW_METADATA_KEYS.has(key)) return;
    if (value === null || value === undefined) return;
    if (typeof value === 'object') {
      formData[key] = getTextFromObjectOrString(value);
      delete copyOfNodeData[key];
    } else {
      formData[key] = value;
    }
  });

  return formData;
};

export const transformFormDataToNodeData = (formData) => {
  const nodeData = {};
  const handledKeys = [];

  if (formData['@id'] !== undefined && formData['@id'] !== null) {
    nodeData['@id'] = formData['@id'];
    handledKeys.push('@id');
  }

  if (typeof formData.name === 'string') {
    nodeData.name = { '@data-comment': '', '#text': formData.name };
    handledKeys.push('name');
  }

  if (typeof formData.type === 'string') {
    nodeData.type = { '@data-comment': '', '#text': formData.type };
    handledKeys.push('type');
  }

  // if object has any keys
  if (formData?.properties && Object.keys(formData?.properties)?.length > 0) {
    nodeData.properties = {
      ...nodeData?.properties,
    };
    Object.entries(formData?.properties).forEach(([key, value]) => {
      if (key === '@data-comment') {
        nodeData.properties[key] = value;
      } else {
        nodeData.properties[key] = { '@data-comment': '', '#text': value };
      }
    });
  }
  handledKeys.push('properties');

  if (formData?.parameters && Object.keys(formData?.parameters)?.length > 0) {
    nodeData.parameters = {
      ...nodeData?.parameters,
      ...formData?.parameters,
    };
  }
  handledKeys.push('parameters');

  // Handle schema - store text content directly (preserve all valid schema content)
  if (formData?.schema !== undefined && formData?.schema !== null) {
    nodeData.schema = formData.schema;
    handledKeys.push('schema');
  }
  handledKeys.push('schema');

  if (Object.keys(formData).length > 0) {
    Object.entries(formData).forEach(([key, value]) => {
      if (!handledKeys.includes(key)) {
        if (key === '@data-comment' || WORKFLOW_METADATA_KEYS.has(key)) {
          nodeData[key] = value;
        } else {
          nodeData[key] = { '@data-comment': '', '#text': value };
        }
      }
    });
  }
  return nodeData;
};

export const transformStatementFormDataToNodeData = (formData) => {
  const nodeData = {};
  const handledKeys = [];

  if (formData['@id'] !== undefined && formData['@id'] !== null) {
    nodeData['@id'] = formData['@id'];
    handledKeys.push('@id');
  }

  if (typeof formData.name === 'string') {
    nodeData.name = formData.name;
    handledKeys.push('name');
  }

  if (typeof formData.type === 'string') {
    nodeData.type = formData.type;
    handledKeys.push('type');
  }

  // if object has any keys
  if (formData?.properties && Object.keys(formData?.properties)?.length > 0) {
    nodeData.properties = {
      ...nodeData?.properties,
      ...formData?.properties,
    };
  }
  handledKeys.push('properties');

  if (formData?.parameters && Object.keys(formData?.parameters)?.length > 0) {
    nodeData.parameters = {
      ...nodeData?.parameters,
      ...formData?.parameters,
    };
  }
  handledKeys.push('parameters');

  // Handle schema - store text content directly
  if (formData?.schema !== undefined && formData?.schema !== null) {
    nodeData.schema = formData.schema;
    handledKeys.push('schema');
  }

  if (Object.keys(formData).length > 0) {
    Object.entries(formData).forEach(([key, value]) => {
      if (!handledKeys.includes(key)) {
        nodeData[key] = value;
      }
    });
  }
  return nodeData;
};
